import { DirectiveBinding, ExtractPropTypes } from 'vue';
import {
  filterOptRestrictions,
  fixed,
  numbersToCurrency,
  setCursor,
  validateRestrictedOptions,
  event,
} from './Utils';
import format from './format';
import unformat from './unformat';
import defaults, { Options } from './options';

// this option is used for ALL directive instances
// let opt: Options = defaults;

const setValue = (
  el: HTMLInputElement,
  opt: Options | ExtractPropTypes<never>,
) => {
  if (!validateRestrictedOptions(opt)) {
    return;
  }

  let positionFromEnd = el.value.length - (el.selectionEnd || 0);

  const formatted = format(el.value, opt);

  if (formatted === el.value) return; // prevent unnecessary updates

  el.value = formatted;

  positionFromEnd = el.value.length - positionFromEnd;
  positionFromEnd = Math.max(positionFromEnd, opt.prefix.length); // left

  setCursor(el, positionFromEnd);

  el.dispatchEvent(event('change')); // v-model.lazy or not
};

const onKeyDown = (e: KeyboardEvent, opt: Options | ExtractPropTypes<never>) => {
  const el = e.currentTarget as HTMLInputElement;

  const backspacePressed = e.code === 'Backspace' || e.code === 'Delete';
  const isAtEndPosition = (el.value.length - (el.selectionEnd || 0)) === 0;

  if (opt.allowBlank
    && backspacePressed
    && isAtEndPosition
    && unformat(el.value, opt) === 0
  ) {
    el.value = '';
    el.dispatchEvent(event('change')); // v-model.lazy
  }
  if (e.key === '+') {
    let number = unformat(el.value, opt);
    if (typeof number === 'string') {
      number = parseFloat(number);
    }
    if (number < 0) {
      el.value = String(number * -1);
    }
  }
};

const onInput = (e: Event, opt: Options | ExtractPropTypes<never>) => {
  const el = e.currentTarget as HTMLInputElement;
  if (/^[1-9]$/.test(el.value)) {
    el.value = numbersToCurrency(el.value, fixed(opt.precision));
  }
  setValue(el, opt);
};

const getValidatedInputElement = (el: HTMLInputElement): HTMLInputElement => {
  if (el.tagName.toLocaleUpperCase() !== 'INPUT') {
    const els = el.getElementsByTagName('input');
    if (els.length !== 1) {
      throw new Error(`Currency Input requires 1 input, found ${els.length} elements.`);
    } else {
       
      return els[0];
    }
  }
  return el;
};

const registerListeners = (el: HTMLInputElement, opt: Options | ExtractPropTypes<never>) => {
  el.onkeydown = (e: KeyboardEvent) => {
    onKeyDown(e, opt);
  };

  el.oninput = (e: Event) => {
    onInput(e, opt);
  };
};

export default {
  mounted(el: HTMLInputElement, binding: DirectiveBinding): void {
    if (!binding.value) {
      return;
    }

    const opt: Options | ExtractPropTypes<never> = filterOptRestrictions({
      ...defaults,
      ...binding.value,
    });

    el = getValidatedInputElement(el);

    registerListeners(el, opt);
    setValue(el, opt);
  },
  updated(el: HTMLInputElement, binding: DirectiveBinding): void {
    if (!binding.value) {
      return;
    }

    const opt: Options | ExtractPropTypes<never> = filterOptRestrictions({
      ...defaults,
      ...binding.value,
    });

    setValue(el, opt);
  },
  beforeUnmount(el: HTMLInputElement): void {
    el.onkeydown = null;
    el.oninput = null;
    el.onfocus = null;
  },
};
